import { motion } from "framer-motion";
import { Shield, Cpu, BookOpen, Heart, GraduationCap, Award } from "lucide-react";
import SEO from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.5 } }),
};

const STATS = [
  { value: "16", label: "Years of Military Service" },
  { value: "$140M+", label: "Aggregate Revenue Managed" },
  { value: "14+", label: "Companies Founded or Acquired" },
  { value: "$175M+", label: "Portfolio Company Value" },
  { value: "2019", label: "Began Studying the Great Wealth Transfer" },
];

const BIO_CARDS = [
  {
    icon: Shield,
    title: "Military & Intelligence",
    body: `Dr. Magee served in the United States Army and Army National Guard for 16 years — from 1986 to 2004 — holding roles that represent the highest levels of military service and specialization.

He served as a Sentinel at the Tomb of the Unknown Soldiers, one of the most demanding and honored posts in the United States military. He was a Member of the First Presidential Honor Guard, providing security for the President of the United States, foreign dignitaries, and VIPs. He served as a Tactical Military Intelligence Officer, a Nuclear Weapons and Strategic Arms Specialist, an Infantry Scout, and a Ranger.

He held a Top Secret/SSBI security clearance. The discipline, analytical rigor, and high-stakes decision-making forged in 16 years of service are the foundation of everything he has built since.`,
  },
  {
    icon: Cpu,
    title: "Technology & Governance",
    body: `Dr. Magee has operated at the highest levels of national security technology for more than three decades — not as an advisor, but as the architect.

He served as Chief Design Architect supporting the Architecture Management and Information Assurance directorates of the Office of the Secretary of Defense Chief Information Office, where he designed the OSD enterprise firewall, created IT change request policies and procedures, and built the architecture framework for the new OSD Enterprise network. He directed the $500M DoD Command Communications Survivability Program at the Pentagon.

Earlier in his career he served at Booz Allen Hamilton as Project Manager and Design Architect, engineering network and security architectures for DoD agencies, and at SAIC as Senior IT Systems Engineer — including managing a Y2K firewall engineering team that deployed 122 firewalls worldwide for a global banking client.

He is the founder of Reform Intelligence®, a technology and AI governance firm serving enterprise and DoD-adjacent clients. His certifications include CISSP, CHS-III, CCIE, CCNP, CCNA, PMP, and more than a dozen others — a credential stack reflecting sustained mastery across technology, security, and enterprise leadership.`,
  },
  {
    icon: BookOpen,
    title: "Publishing & Financial Literacy",
    body: `Dr. Magee is the founder of Reform Publishing LLC, distributed by Phoenix Marketing & Distribution LLC. His catalog spans nine published titles across two series — with a flagship forthcoming title that represents the culmination of research begun in 2019.

The Financial Literacy Series includes Understanding the Numbers™ (2nd Ed.), validated as a First Step Act program reaching incarcerated individuals and reentry populations across the country, as well as In Wealth We Trust, The 12 Tenets of the Richest Man in Babylon, and Poverty Is A Mindset, Wealth Is A System.

The Communication Mastery Series™ is a six-volume progression through the levels of human communication — The Art of Small Talk, The Art of Conversation, The Art of Listening, The Art of Asking Questions, The Art of Persuasion, and The Art of Negotiation — with The Art of Silence as an advanced companion volume.

His forthcoming book, THE GREAT WEALTH TRANSFER™, is the first comprehensive acquisition playbook written specifically for minority and underserved entrepreneurs — drawing on research that began in 2019, years before the topic entered the mainstream conversation.`,
  },
  {
    icon: Heart,
    title: "Advocacy & Community",
    body: `Dr. Magee is the founder of VALOR (Veterans' Alliance for Legislation & Organizational Reform, Inc.), a 501(c)(3) nonprofit headquartered in Washington, DC. Through VALORLink™, VALOR connects veterans in crisis with trained fellow veteran volunteers in real time, synchronized with the VA Crisis Hotline.

He is also the founder of the Minority Owned Business Coalition, Inc. (MOBC), a certified 501(c)(3) nonprofit organized since 2003 to provide financial assistance, endowments, scholarships, and mentorship to companies, youth, and underserved communities across the country.`,
  },
];

const EDUCATION = [
  "Doctor of Philosophy in Management Information Systems — Kennedy-Western University (2006)",
  "MS in Information Systems & Telecommunications — The Johns Hopkins University (2003)",
  "BS in Computer Information Systems — Strayer University (1999)",
  "Graduate, Officer Candidate School — Virginia Army National Guard (2002)",
];

const CERTIFICATIONS = [
  "CISSP — Certified Information Systems Security Professional",
  "CHS-III — Certified Homeland Security (American College of Forensic Examiners)",
  "PMP — Project Management Professional",
  "CCIE — Cisco Certified Internetwork Expert",
  "CCNP — Cisco Certified Network Professional",
  "CCNA — Cisco Certified Network Associate",
  "CNE — Certified Network Engineer",
  "CNA — Certified Network Administrator",
  "Certified Financial Analyst/Planner",
  "VIP-Certified Professional (National Center for Veteran Institute for Procurement)",
  "DITSCAP — DoD Information Technology Security Certification and Accreditation Process",
  "DIACAP — DoD Information Assurance Certification and Accreditation Process",
];

const MEMBERSHIPS = [
  "Member, MENSA International",
  "Omega Psi Phi Fraternity, Inc.",
  "NAACP",
  "AFCEA (Armed Forces Communications and Electronics Association)",
  "IEEE",
  "American Small Business Coalition",
  "Small Emerging Contractors Advisory Forum",
  "American College of Forensic Examiners",
  "Northern Virginia Technology Council",
];

const PHILOSOPHY = [
  "Inspiration without implementation is entertainment. I write books that change behavior, not just beliefs.",
  "The data has always been there. The question is whether people with the right information are willing to act on it.",
  "Technology should serve the vulnerable, not just the powerful. That's true in AI governance and it's true in veteran crisis response.",
  "Generational wealth is not built in one lifetime. It's built in one decision — made in the right lifetime.",
];

export default function About() {
  return (
    <div>
      {/* HERO QUOTE */}
      <SEO
        title="About"
        description="Dr. Ben Magee has spent his career at the intersection of intelligence, technology, and community. U.S. Army veteran, PhD, CISSP, MENSA member, serial entrepreneur, and nonprofit founder."
        keywords="Dr. Ben Magee biography, military intelligence, AI governance, CISSP, MENSA, veteran entrepreneur, Reform Intelligence, VALOR"
        page="about"
        canonical="https://drbenmagee.com/about"
      />
      <section className="bg-[#1B2A4A] py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="w-1 h-12 bg-[#C9A84C] mb-8" />
          <motion.blockquote
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-heading text-2xl sm:text-3xl lg:text-4xl text-white italic leading-snug tracking-tight"
            data-testid="about-hero-quote"
          >
            "Dr. Ben Magee has spent his career at the intersection of intelligence, technology, and community &mdash; building systems that protect, platforms that empower, and books that equip."
          </motion.blockquote>
        </div>
      </section>

      {/* BY THE NUMBERS */}
      <section className="py-12 md:py-16 bg-[#0d1b2e]" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-8 text-center">By the Numbers</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {STATS.map((stat, i) => (
              <motion.div
                key={stat.label}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="bg-[#1B2A4A] border border-[#C9A84C]/20 rounded-sm p-6 text-center"
              >
                <p className="font-heading font-black text-3xl md:text-4xl text-[#C9A84C] mb-2">{stat.value}</p>
                <p className="text-xs text-white/70 leading-tight">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* THE ORIGIN STORY */}
      <section className="py-20 md:py-32 bg-white" data-testid="about-origin">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">The Origin Story &mdash; Why 2019 Matters</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight mb-8">
            He Was Studying This Before It Had a Name
          </h2>
          <div className="text-[#1B2A4A]/70 text-base leading-relaxed space-y-6">
            <p>
              In 2019, Dr. Ben Magee began studying a phenomenon that had not yet entered the mainstream conversation: the largest generational transfer of wealth in American history. While most of the country was unaware of the term "Great Wealth Transfer," Dr. Magee was already mapping its implications for minority entrepreneurs &mdash; analyzing the data, identifying the gaps in existing financial empowerment frameworks, and developing the blueprint that would become his forthcoming book, THE GREAT WEALTH TRANSFER&trade;.
            </p>
            <p className="font-semibold text-[#1B2A4A]">He was not responding to a trend. He was ahead of one.</p>
          </div>
        </div>
      </section>

      {/* BIOGRAPHY CARDS */}
      <section className="py-20 md:py-32 bg-[#E8EDF5]" data-testid="about-biography">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">Full Biography</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight mb-12">
            Four Decades of Building
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {BIO_CARDS.map((card, i) => (
              <motion.div
                key={card.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={fadeUp}
                className="bg-white border border-[#1B2A4A]/10 rounded-sm p-8"
              >
                <card.icon className="h-8 w-8 text-[#C9A84C] mb-4" />
                <h3 className="font-heading font-semibold text-xl text-[#1B2A4A] mb-4">{card.title}</h3>
                <div className="text-sm text-[#1B2A4A]/70 leading-relaxed space-y-4">
                  {card.body.split('\n\n').map((para, idx) => (
                    <p key={`${card.title}-para-${idx}`}>{para}</p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CREDENTIALS & AFFILIATIONS */}
      <section className="py-20 md:py-32 bg-white" data-testid="about-credentials">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center gap-4 mb-10">
            <Award className="h-8 w-8 text-[#C9A84C]" />
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C]">Credentials & Affiliations</p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight">Certifications & Education</h2>
            </div>
          </div>
          
          <div className="space-y-10">
            {/* Education */}
            <div>
              <h3 className="font-heading font-semibold text-lg text-[#1B2A4A] mb-4 uppercase tracking-wide">Education</h3>
              <div className="space-y-3">
                {EDUCATION.map((item) => (
                  <div key={item} className="flex items-start gap-3 p-4 bg-[#E8EDF5] rounded-sm border border-[#1B2A4A]/10">
                    <GraduationCap className="h-5 w-5 text-[#C9A84C] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#1B2A4A]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div>
              <h3 className="font-heading font-semibold text-lg text-[#1B2A4A] mb-4 uppercase tracking-wide">Certifications (Selected)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CERTIFICATIONS.map((item) => (
                  <div key={item} className="flex items-start gap-3 p-3 bg-[#F5EDD6] rounded-sm border border-[#C9A84C]/20">
                    <div className="w-2 h-2 bg-[#C9A84C] rounded-full flex-shrink-0 mt-1.5" />
                    <span className="text-sm text-[#1B2A4A]/80">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Memberships */}
            <div>
              <h3 className="font-heading font-semibold text-lg text-[#1B2A4A] mb-4 uppercase tracking-wide">Memberships</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {MEMBERSHIPS.map((item) => (
                  <div key={item} className="flex items-start gap-3 p-3 bg-[#E8EDF5] rounded-sm border border-[#1B2A4A]/10">
                    <div className="w-2 h-2 bg-[#1B2A4A] rounded-full flex-shrink-0 mt-1.5" />
                    <span className="text-sm text-[#1B2A4A]/80">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Security Clearance */}
            <div>
              <h3 className="font-heading font-semibold text-lg text-[#1B2A4A] mb-4 uppercase tracking-wide">Security Clearance</h3>
              <div className="flex items-center gap-3 p-4 bg-[#1B2A4A] rounded-sm">
                <Shield className="h-5 w-5 text-[#C9A84C] flex-shrink-0" />
                <span className="text-sm text-white font-medium">Top Secret/SSBI (Inactive)</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-20 md:py-32 bg-[#F5EDD6]" data-testid="about-philosophy">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">Philosophy</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight mb-12">
            Principles That Drive the Work
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {PHILOSOPHY.map((q, i) => (
              <motion.blockquote
                key={`philosophy-${i}-${q.substring(0, 20)}`}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="bg-white border-l-4 border-[#C9A84C] p-8 rounded-sm"
              >
                <p className="font-heading text-lg text-[#1B2A4A] italic leading-relaxed">"{q}"</p>
              </motion.blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* PERSONAL */}
      <section className="py-20 md:py-32 bg-white" data-testid="about-personal">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row items-center gap-10">
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="absolute -inset-1 bg-[#C9A84C]/20 rounded-sm blur-sm" />
                <img
                  src="https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/d5hw9875_9D68558F-415B-4B81-BC77-5CFE62AE5B4C.png"
                  alt="Dr. Ben Magee"
                  data-testid="about-headshot"
                  className="relative w-48 h-48 object-cover object-top rounded-sm border-2 border-[#C9A84C]/30"
                />
              </div>
            </div>
            <div className="text-center md:text-left">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9A84C] mb-4">Personal</p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A4A] tracking-tight mb-6">
                The Person Behind the Credentials
              </h2>
              <p className="text-base text-[#1B2A4A]/70 leading-relaxed max-w-2xl">
                Dr. Magee is married to Marie, with whom he has built a family over 35 years. He is based in Virginia. He is a builder &mdash; of companies, of communities, of platforms, and of people. Everything he writes, he has lived.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
